<template>
  <div class="checkout-summary-wrapper">
    <div class="plan-details">
      <h1 class="header-2">{{ $t('profile.planSummary') }}</h1>
      <p class="body-text gray-text ">{{ plan?.title }}</p>
      <p class="header-2">{{ displayPrice }}</p>
      <p v-if="isAnnual && typeof plan?.price === 'number'" class="caption gray-text annual-text">
        {{ $t('plans.billedAnnually', { price: annualPrice }) }}
      </p>
      <p class="caption gray-text ">{{ plan?.description }}</p>
      <ul class="caption gray-text">
        <li v-for="(feature, index) in plan?.features" :key="index">
          {{ feature }}
        </li>
      </ul>
    </div>
    <div class="payment-terms">
      <div id="paypal-button-container"></div>
    </div>

    <div v-if="showPopup" class="status-popup-overlay" @click="closePopup">
      <div class="status-popup" @click.stop>
        <div class="status-icon" :class="{ success: !isError }">
          <BaseIcon :iconName="isError ? 'error' : 'check'" :color="isError ? '#ff6b6b' : '#20AD8B'" />
        </div>
        <h3>{{ popupTitle }}</h3>
        <p>{{ popupMessage }}</p>
        <button class="close-btn" @click="closePopup">{{ $t('checkout.close') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { onMounted, computed, ref } from "vue";
import { plans } from "@/constants/plans";
import BaseIcon from '@/components/BaseComponents/BaseIcon.vue';
import { trackEvent } from "@/utils/analytics";
import { useI18n } from 'vue-i18n';
import { localizePlan } from '@/i18n/planCatalog';

const router = useRouter();
const store = useStore();
const { t, tm } = useI18n();
const selectedPlan = store.state.selectedPlan;
const isAnnual = computed(() => store.state.isAnnual);
const userDetails = computed(() => store.state.userDetails);

const showPopup = ref(false);
const isError = ref(false);
const popupTitle = ref('');
const popupMessage = ref('');

const plan = computed(() => {
  const planDetails = localizePlan(plans[selectedPlan.title], t, tm);
  const currentPlanName = userDetails.value?.current_subscription_plan;
  
  return {
    ...planDetails,
    price: isAnnual.value ? planDetails.priceAnnual : planDetails.priceMonthly,
    displayName: planDetails.displayNames[isAnnual.value ? 'annual' : 'monthly'],
    currentPlan: currentPlanName || 'Freemium'
  };
});

const annualPrice = computed(() => {
  return plan.value?.price ? plan.value.price * 12 : 0;
});

const displayPrice = computed(() =>
  typeof plan.value?.price === 'number'
    ? t('plans.monthlyPrice', { price: plan.value.price })
    : plan.value?.price || ''
);

function getCheckoutAnalyticsContext() {
  return {
    selected_plan: plan.value?.title || selectedPlan?.title || "unknown",
    billing_cycle: isAnnual.value ? "annual" : "monthly",
    price: plan.value?.price ?? null,
    previous_plan: userDetails.value?.current_subscription_plan || "Freemium",
  };
}

function showStatusPopup(success, title, message) {
  isError.value = !success;
  popupTitle.value = title;
  popupMessage.value = message;
  showPopup.value = true;
}

function closePopup() {
  showPopup.value = false;
  if (!isError.value) {
    store.dispatch('fetchUserDetails')
      .then(() => {
        router.push('/app/home');
      })
      .catch(error => {
        console.error('Failed to fetch updated user details:', error);
        router.push('/app/home');
      });
  }
}

onMounted(() => {
  trackEvent("checkout_page_viewed", getCheckoutAnalyticsContext());

  if (window.paypal) {
    renderPayPalButton();
  }
});

function renderPayPalButton() {
  const planId = plan.value.paypalPlanId[isAnnual.value ? 'annual' : 'monthly'];
  const userId = userDetails.value?.user_id;
  
  if (!planId) {
    showStatusPopup(false, t('checkout.error'), t('checkout.invalidPlan'));
    return;
  }

  if (!userId) {
    showStatusPopup(false, t('checkout.error'), t('checkout.noUser'));
    return;
  }

  window.paypal
    .Buttons({
      createSubscription(data, actions) {
        trackEvent("payment_initiated", {
          ...getCheckoutAnalyticsContext(),
          payment_method: "paypal",
        });

        return actions.subscription.create({
          plan_id: planId,
          custom_id: userId.toString(),
        });
      },
      // eslint-disable-next-line no-unused-vars
      onApprove(data) {
        trackEvent("subscription_created", getCheckoutAnalyticsContext());

        store.dispatch('fetchUserDetails')
          .then(() => {
            showStatusPopup(
              true,
              t('checkout.successTitle'),
              t('checkout.success')
            );
          })
          .catch(error => {
            console.error('Failed to fetch updated user details:', error);
            showStatusPopup(
              true,
              t('checkout.successTitle'),
              t('checkout.successRefresh')
            );
          });
      },
      onError(err) {
        console.error("PayPal Error:", err);
        trackEvent("payment_failed", {
          ...getCheckoutAnalyticsContext(),
          payment_method: "paypal",
        });
        showStatusPopup(
          false,
          t('checkout.failedTitle'),
          t('checkout.failed')
        );
      },
      style: {
        color: 'black',
      }
    })
    .render("#paypal-button-container");
}
</script>

<style scoped>
.checkout-summary-wrapper {
  display: flex;
  background-color: var(--bg-card);
  border-radius: var(--border-radius-2xl);
  position: relative;
}

.payment-terms,
.plan-details {
  padding: var(--spacing-3xl);
  width: 50%;
}

.payment-terms {
  background-color: var(--bg-card);
  border-radius: var(--border-radius-2xl);
}

/* Status Popup Styles */
.status-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-dropdown);
  backdrop-filter: var(--backdrop-blur-sm);
}

.status-popup {
  background: var(--bg-cookie-modal);
  padding: var(--spacing-3xl);
  border-radius: var(--border-radius-xl);
  text-align: center;
  max-width: 400px;
  width: 90%;
}

.status-icon {
  margin-bottom: var(--spacing-xl);
  display: flex;
  justify-content: center;
}

.status-icon.success {
  color: var(--color-success);
}

.status-popup h3 {
  margin: 0 0 var(--spacing-xl) 0;
  color: var(--color-white);
}

.status-popup p {
  margin: 0 0 var(--margin-lg) 0;
  color: var(--color-white-70);
}

.close-btn {
  padding: var(--padding-btn-lg);
  border-radius: var(--border-radius-md);
  background: var(--gradient-success);
  color: var(--color-white);
  border: none;
  cursor: pointer;
  transition: var(--transition-normal);
}

.close-btn:hover {
  opacity: var(--opacity-hover);
}

.annual-text {
  margin-top: -8px;
  margin-bottom: var(--spacing-xl);
}
</style>
